import apiClient from './client';
import { CrawlResponse } from '../types';
import { normalizeDate, validateSport, sanitizeParam } from './validation';

// ===== Database Query API =====

export const queryMatchesFromDB = async (
  sport: string,
  date: string,
  league?: string
): Promise<CrawlResponse> => {
  try {
    // Validate inputs
    const validatedSport = validateSport(sport);
    const normalizedDate = normalizeDate(date);

    const params = new URLSearchParams({
      sport: validatedSport,
      date: normalizedDate
    });

    if (league && league.trim()) {
      params.append('league', sanitizeParam(league) || '');
    }

    const response = await apiClient.get(`/api/query/matches?${params}`);
    return response.data;
  } catch (error: any) {
    // Handle validation errors
    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    // Handle specific HTTP status codes
    if (error.response?.status === 404) {
      // Return empty result instead of throwing error
      return {
        status: 'success',
        count: 0,
        data: []
      };
    }

    // Re-throw other errors
    throw error;
  }
};

// Query upcoming matches (today and future)
export const queryUpcomingMatches = async (
  fromDate?: string,
  toDate?: string,
  sport?: string
): Promise<CrawlResponse> => {
  try {
    const params = new URLSearchParams();

    if (fromDate) {
      params.append('from_date', normalizeDate(fromDate));
    }

    if (toDate) {
      params.append('to_date', normalizeDate(toDate));
    }

    if (sport) {
      params.append('sport', validateSport(sport));
    }

    const response = await apiClient.get(`/api/query/upcoming-matches?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    // Handle validation errors
    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    // Handle specific HTTP status codes
    if (error.response?.status === 404) {
      // Return empty result instead of throwing error
      return {
        status: 'success',
        count: 0,
        data: []
      };
    }

    // Re-throw other errors
    throw error;
  }
};

// ===== Reports API =====

export interface ReportItem {
  prediction_id: number;
  match_id: number;
  ai_model: string;
  version: number;
  report: string;
  full_report: string;
  created_at: string | null;
  admin_id: number | null;
  admin_name: string | null;
  notion_admin_id: number | null;
  notion_admin_name: string | null;
  home_team: string;
  away_team: string;
  match_date: string;
  sport: string;
}

export const getReports = async (params: {
  date?: string;          // deprecated, use created_date
  created_date?: string;  // 작성일 기준 조회
  sport?: string;
  admin_id?: number;
  ai_model?: string;
  limit?: number;
  offset?: number;
}): Promise<{
  status: string;
  total: number;
  count: number;
  offset: number;
  limit: number;
  data: ReportItem[];
}> => {
  const queryParams: any = {};
  if (params.created_date) queryParams.created_date = params.created_date.replace(/-/g, '');
  else if (params.date) queryParams.date = params.date.replace(/-/g, '');
  if (params.sport) queryParams.sport = params.sport;
  if (params.admin_id) queryParams.admin_id = params.admin_id;
  if (params.ai_model) queryParams.ai_model = params.ai_model;
  if (params.limit) queryParams.limit = params.limit;
  if (params.offset !== undefined) queryParams.offset = params.offset;

  const response = await apiClient.get('/api/query/reports', { params: queryParams });
  return response.data;
};
